import { datadogLogs, LogsInitConfiguration } from '@datadog/browser-logs';
import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';

import packageJson from '../../../package.json';
import { environment } from '../../environments/environment';
import { EnvName, getEnvironmentName } from '../core/helpers/environment-name';
import { isBrowserExtensionCspViolation } from './is-browser-extension-csp-violation.util';
import { isDatadogSdkInternalError } from './is-datadog-sdk-internal-error.util';
import { isExpectedApiMe401Error } from './is-expected-api-me-401-error.util';
import { isThirdPartyScriptError } from './is-third-party-script-error.util';
import { isTransportAbortError } from './is-transport-abort-error.util';

const version = GIT_COMMIT_SHA || packageJson.version;

const DATADOG_SITE = 'datadoghq.eu';
const SERVICE = 'webds-web';

const ALLOWED_TRACING_URLS_BY_ENV: Record<EnvName, string[]> = {
    prod: ['https://showroom.ecco.com', 'https://api.showroom.ecco.com'],
    uat: ['https://uat.showroom.ecco.com', 'https://uat-api.showroom.ecco.com'],
    test: ['https://test.showroom.ecco.com', 'https://test-api.showroom.ecco.com'],
    dev: ['https://dev.showroom.ecco.com', 'https://dev-api.showroom.ecco.com'],
    local: [],
};

// 1-month baseline period (target revert 2026-06-07): prod bumped to 100%
// for full data collection; dev muted to 25% as it is volatile and rarely used.
const SESSION_SAMPLE_RATE_BY_ENV: Record<EnvName, number> = {
    prod: 100,
    uat: 100,
    test: 100,
    dev: 25,
    local: 100,
};

// Session Replay sampling, per env. 0-100 scale, applied on top of sessionSampleRate
// (effective replay = sessionSampleRate% x this%). Replay is billed per recorded
// session, so we only record where we debug real issues (prod/uat); off elsewhere.
const SESSION_REPLAY_SAMPLE_RATE_BY_ENV: Record<EnvName, number> = {
    prod: 100,
    uat: 100,
    test: 0,
    dev: 0,
    local: 0,
};

let initialised = false;

export const initializeDatadog = (): void => {
    if (initialised) return;
    if (typeof window === 'undefined') return;

    const env = getEnvironmentName();
    const allowedTracingUrls = ALLOWED_TRACING_URLS_BY_ENV[env];
    const sessionSampleRate = SESSION_SAMPLE_RATE_BY_ENV[env];
    const sessionReplaySampleRate = SESSION_REPLAY_SAMPLE_RATE_BY_ENV[env];
    const loggerLevel = env === 'prod' ? 'info' : 'debug';

    const rumParams: RumInitConfiguration = {
        applicationId: environment.datadog.rumApplicationId,
        clientToken: environment.datadog.rumClientToken,
        site: DATADOG_SITE,
        service: SERVICE,
        env,
        version,
        sessionSampleRate,
        sessionReplaySampleRate,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        allowedTracingUrls,
        beforeSend: (event) =>
            !isExpectedApiMe401Error(event) &&
            !isDatadogSdkInternalError(event) &&
            !isThirdPartyScriptError(event) &&
            !isBrowserExtensionCspViolation(event) &&
            !isTransportAbortError(event),
    };

    const logsParams: LogsInitConfiguration = {
        clientToken: environment.datadog.logsClientToken,
        site: DATADOG_SITE,
        service: SERVICE,
        env,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: [],
        forwardReports: [],
        sessionSampleRate: 100,
    };

    datadogRum.init(rumParams);
    datadogLogs.init(logsParams);
    datadogLogs.logger.setLevel(loggerLevel);

    initialised = true;
};
